import type { MouseEvent as ReactMouseEvent } from "react";
import React from "react";
import clsx from "clsx";
import { PlayIcon } from "@public/images/svg/PlayIcon";
import { BookIcon } from "@public/images/svg/BookIcon";
import { CheckIcon } from "@public/images/svg/CheckIcon";
import { FinishIcon } from "@public/images/svg/FinishIcon";
import { LogoutIcon } from "@public/images/svg/LogoutIcon";
import { ArticleIcon } from "@public/images/svg/ArticleIcon";
import { CreditCardIcon } from "@public/images/svg/CreditCardIcon";
import { SupportIcon } from "@public/images/svg/SupportIcon";
import { SettingsIcon } from "@public/images/svg/SettingsIcon";
import { BillingHistoryIcon } from "@public/images/svg/BillingHistoryIcon";
import { MobileMenuButton } from "@public/images/svg/MobileMenuButton";
import { CloseIcon } from "@public/images/svg/CloseIcon";
import { NotificationsIcon } from "@public/images/svg/NotificationsIcon";
import { AlarmClockIcon } from "@public/images/svg/AlarmClockIcon";

type Stay =
    | "play"
    | "book"
    | "check"
    | "finish"
    | "logout"
    | "policy"
    | "daily_goal"
    | "subscription"
    | "support"
    | "settings"
    | "billing_history"
    | "notifications"
    | "mobile_menu"
    | "close_icon";
type Color = "base" | "blue" | "white" | "green" | "pink";

export enum ButtonType {
    BUTTON = "BUTTON",
    BUTTON_BASE = "BUTTON_BASE",
    BUTTON_TEXT = "BUTTON_TEXT",
    BUTTON_CHIPS = "BUTTON_CHIPS",
    ICON_BUTTON = "ICON_BUTTON",
    ICON_TEXT_BUTTON = "ICON_TEXT_BUTTON",
    HOVER_TOGGLE_TEXT_BUTTON = "HOVER_TOGGLE_TEXT_BUTTON",
    NONE = "NONE"
}

export type ButtonProps = {
    type: ButtonType;
    full?: boolean;
    children?: React.ReactNode | string;
    marginTop?: number;
    marginBottom?: number;
    small?: boolean;
    disabled?: boolean;
    typeOfButton?: "button" | "submit" | "reset";
    onClick?: (event: ReactMouseEvent<HTMLButtonElement>) => void;
    color?: Color;
    isSelected?: boolean;
    capitalize?: boolean;
    stay?: Stay;
    className?: string;
    pulse?: boolean;
    /** Primary call to action: a gentle shiver every few seconds invites the tap (CSS only). */
    shiver?: boolean;
    widthClass?: string;
    heightClass?: string;
};

export const Button = ({
    type,
    full,
    children,
    marginTop,
    marginBottom,
    small,
    disabled,
    typeOfButton = "button",
    onClick,
    color = "base",
    isSelected,
    capitalize,
    stay = "play",
    className = "",
    pulse = false,
    shiver = false,
    widthClass,
    heightClass
}: ButtonProps) => {
    const colorMap: Record<Color | Stay, string> = {
        base: "text-blue-100 bg-white-700 enabled:md:hover:bg-white-800 enabled:active:bg-white-600 enabled:md:active:bg-white-600 disabled:bg-white-600",
        // White text on brand surfaces is `text-white` (literal), not `text-white-900` (flips in dark).
        blue: "text-white bg-blue-600 enabled:md:hover:bg-blue-hover enabled:active:bg-blue-active enabled:md:active:bg-blue-active disabled:bg-blue-600",
        white: "text-blue-100 bg-white-900 enabled:md:hover:bg-white-800 enabled:active:bg-white-600 enabled:md:active:bg-white-700 disabled:bg-white-600",
        pink: "text-white bg-pink-500 enabled:md:hover:bg-pink-600 enabled:active:bg-pink-700 enabled:md:active:bg-pink-700 disabled:bg-pink-500",
        play: "bg-blue-600 md:hover:bg-blue-hover active:bg-blue-active md:active:bg-blue-active text-white active:text-blue-700",
        book: "bg-white-700 md:hover:bg-white-900 active:bg-white-active md:active:bg-white-active text-white-500 active:text-white-text-active",
        check: "bg-green-500 md:hover:bg-green-600 active:bg-green-active md:active:bg-green-active text-white active:text-green-700",
        finish: "bg-white-700 md:hover:bg-white-900 active:bg-white-active md:active:bg-white-active text-white-500 active:text-white-text-active",
        logout: "bg-white-900 focus:bg-white-600",
        green: "border-2 border-green-600 bg-white-900 hover:bg-green-800 active:bg-green-700 shadow-none text-white-300",
        policy: "",
        subscription: "",
        support: "",
        settings: "",
        billing_history: "",
        notifications: "",
        mobile_menu: "",
        close_icon: "",
        daily_goal: ""
    };

    const iconMap: Record<Stay, React.ReactNode> = {
        play: <PlayIcon />,
        book: <BookIcon />,
        check: <CheckIcon color="currentColor" />,
        finish: <FinishIcon />,
        logout: <LogoutIcon />,
        policy: <ArticleIcon />,
        subscription: <CreditCardIcon />,
        support: <SupportIcon />,
        settings: <SettingsIcon />,
        billing_history: <BillingHistoryIcon />,
        notifications: <NotificationsIcon />,
        mobile_menu: <MobileMenuButton />,
        close_icon: <CloseIcon />,
        daily_goal: <AlarmClockIcon />
    };

    switch (type) {
        case ButtonType.BUTTON: {
            return (
                <button
                    type={typeOfButton}
                    className={`
        box-border w-full flex justify-center items-center rounded-[20px]
        border-transparent text-center font-bold
        cursor-pointer transition-colors duration-300 ${className}
        ${
            disabled
                ? "bg-gray-100 text-gray-600 cursor-not-allowed"
                : "bg-radial-gradient text-gray-800 hover:bg-hover-radial-gradient"
        }
        ${
            small
                ? "max-w-fit h-[50px] text-lg"
                : full
                  ? "max-w-[510px] h-[72px] text-[22px]"
                  : "max-w-[510px] md:max-w-[320px] h-[72px] text-[22px]"
        }
      `}
                    style={{
                        marginTop: marginTop ? `${marginTop}px` : "0px",
                        marginBottom: marginBottom ? `${marginBottom}px` : "0px"
                    }}
                    disabled={disabled}
                    onClick={onClick}
                >
                    {children}
                </button>
            );
        }

        case ButtonType.BUTTON_BASE: {
            return (
                <button
                    type={typeOfButton}
                    disabled={disabled}
                    className={clsx(
                        "w-full enabled:cursor-pointer disabled:cursor-not-allowed mt-4 px-5 pt-[13px] pb-[15px] flex justify-center items-center gap-1.5 rounded-xl text-[18px] font-bold leading-[24px] shadow-main-button transition-all duration-100 ease-in enabled:active:scale-[0.98]",
                        colorMap[color],
                        disabled && "opacity-30",
                        shiver && !disabled && "cta-shiver",
                        className
                    )}
                    onClick={onClick}
                    style={pulse ? { animation: "pulseCustom 1.4s ease-in-out infinite" } : undefined}
                >
                    {children}
                </button>
            );
        }

        case ButtonType.BUTTON_TEXT: {
            return (
                <button
                    type={typeOfButton}
                    disabled={disabled}
                    className={clsx(
                        "w-full cursor-pointer mt-4 px-4 pt-3 pb-4 flex justify-center items-center gap-1.5 rounded-xl text-[18px] font-bold leading-[24px]",
                        colorMap[color],
                        color === "base" && "border-2 border-white-600 border-solid  border-b-0",
                        disabled && "opacity-30",
                        className
                    )}
                    onClick={onClick}
                >
                    {children}
                </button>
            );
        }

        case ButtonType.BUTTON_CHIPS: {
            return (
                <div
                    className={`w-min m-0 mb-2 me-2 rounded-[50px] transition-all ${
                        isSelected ? "bg-gray-800" : "bg-transparent"
                    }`}
                >
                    <button
                        disabled={disabled}
                        type={typeOfButton}
                        className={`rounded-[50px] cursor-pointer border-2 transition-all whitespace-nowrap 
          ${
              isSelected
                  ? "border-gray-800 bg-gray-800 bg-clip-text text-transparent bg-custom-gradient"
                  : "border-gray-200 bg-transparent bg-clip-border text-gray-500"
          }
          ${small ? "h-[36px] px-3 text-sm font-medium" : "px-4 py-3 text-lg font-bold"}
          transition-colors duration-300 ease-in-out`}
                        onClick={onClick}
                    >
                        {capitalize
                            ? String(children).charAt(0).toUpperCase() + String(children).slice(1)
                            : String(children).toLowerCase()}
                    </button>
                </div>
            );
        }

        case ButtonType.ICON_BUTTON: {
            return (
                <button
                    disabled={disabled}
                    onClick={onClick}
                    type={typeOfButton}
                    className={clsx(
                        "h-[72px] w-[88px] flex items-center justify-center rounded-[20px] shadow-main-button transition-transform duration-100 ease-in enabled:active:scale-[0.98]",
                        colorMap[stay],
                        className,
                        disabled && "opacity-40"
                    )}
                >
                    {iconMap[stay]}
                </button>
            );
        }

        case ButtonType.ICON_TEXT_BUTTON: {
            return (
                <button
                    disabled={disabled}
                    onClick={onClick}
                    type={typeOfButton}
                    className={clsx(
                        "p-4 w-full cursor-pointer rounded-xl flex flex-row gap-3 items-center border-white-700 border-2 mt-4 font-semibold",
                        colorMap[color],
                        className
                        // colorMap[stay]
                    )}
                >
                    {iconMap[stay]}
                    <span>{children}</span>
                </button>
            );
        }

        case ButtonType.HOVER_TOGGLE_TEXT_BUTTON: {
            const textOne = Array.isArray(children) ? children[0] : children;
            const textTwo = Array.isArray(children) ? children[1] : null;

            return (
                <button
                    type={typeOfButton}
                    disabled={disabled}
                    onClick={onClick}
                    className={clsx(
                        "relative inline-grid place-items-center rounded-full text-white cursor-pointer overflow-hidden transition-colors duration-300 ease-in-out hover:bg-blue-700 active:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B9BFC] hover:[&_.btn-text-one]:-translate-y-full hover:[&_.btn-text-one]:opacity-0 hover:[&_.btn-text-two]:translate-y-0 hover:[&_.btn-text-two]:opacity-100",
                        colorMap[color],
                        color === "base" && "border-2 border-white-600 border-solid border-b-0",
                        "w-fit px-5 bp-960:px-6 py-2.5 bp-960:py-3.5 h-[44px] bp-960:h-[52px] rounded-[34px]",
                        className
                    )}
                >
                    <span className="invisible whitespace-nowrap">
                        {textTwo && String(textTwo).length > String(textOne).length ? textTwo : textOne}
                    </span>
                    <span className="btn-text-one absolute transition-all duration-300 ease-out translate-y-0 opacity-100 will-change-transform will-change-opacity whitespace-nowrap">
                        {textOne}
                    </span>
                    <span className="btn-text-two absolute transition-all duration-300 ease-out translate-y-full opacity-0 will-change-transform will-change-opacity whitespace-nowrap">
                        {textTwo}
                    </span>
                </button>
            );
        }

        case ButtonType.NONE: {
            return (
                <button
                    type={typeOfButton}
                    disabled={disabled}
                    className={clsx(disabled && "opacity-30", className)}
                    onClick={onClick}
                >
                    {children}
                </button>
            );
        }
    }
};
